<template>
  <div class="menu-list-entry" :class="{ 'gold-border-box': hasPrio }">
    <b-row
      class="announcement-entry mx-auto w-100 p-3"
      :class="{ expanded: expanded }"
      align-h="center"
    >
      <b-col cols="3" class="d-flex flex-column align-items-center">
        <img
          draggable="false"
          class="news-icon"
          :class="{ clickable: customLink }"
          :src="newsEntryImg"
          @click="openCustomLink"
        />
      </b-col>

      <b-col class="ps-0 pt-2 d-flex flex-column justify-content-center">
        <div class="news-text">
          <div class="headline d-flex align-items-center">
            <div v-if="isUnread" class="new-message-icon"></div>

            <span
              :class="{ highlight: hasPrio }"
              v-html="$t(entry.title)"
            ></span>
            <span class="timestamp ms-auto">{{ entryTimestamp }}</span>
          </div>
          <div class="subtext">
            <span v-html="newsEntryText"> </span>
            <button
              v-if="(entry.markdown_content || '').length > maxCharacters"
              class="button-blue-modern ms-3"
              style="font-size: 12px; padding: 4px 12px"
              @click="handleExpand"
            >
              {{ expandButtonText }}
            </button>
            <!-- entry images -->
            <template v-if="hasGallery && expanded">
              <div
                class="w-100 d-inline-flex align-items-center news-gallery mt-3 p-3"
              >
                <img
                  v-for="(image, index) in entry.gallery"
                  :key="'gallery-image' + index"
                  :alt="image.name"
                  class="news-gallery-image me-2 clickable"
                  :src="image.url"
                  @click="setLargeImage(image.url)"
                />
              </div>
              <b-modal
                v-model="showLargeImage"
                hide-header
                hide-footer
                lazy
                modal-class="image-modal"
                dialog-class="image-modal-dialog"
                content-class="image-modal-content"
                body-class="image-modal-body"
              >
                <img :src="largeImgUrl" class="image-modal-img" />
              </b-modal>
            </template>
            <!-- related links section -->
            <template
              v-if="
                expanded && entry.links && Object.keys(entry.links).length > 0
              "
            >
              <div class="related-links mt-4 p-3">
                <h4
                  class="related-title text-white mb-3 d-flex align-items-center"
                >
                  <img
                    src="@/assets/img/icons/link.png"
                    alt="link icon"
                    class="related-icon me-2"
                  />
                  <span>Related Links</span>
                </h4>

                <div class="link-card-grid">
                  <div
                    v-for="(title, url, index) in entry.links"
                    :key="'related-link-' + index"
                    class="link-card"
                    @click="openLink(url)"
                  >
                    <div class="icon-circle me-3">
                      <img
                        :src="getFavicon(url)"
                        alt="favicon"
                        class="favicon"
                        @error="onFaviconError"
                      />
                    </div>
                    <div class="text">
                      <div class="title">{{ title }}</div>
                      <div class="url">{{ getHostname(url) }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </template>
            <!-- in case reward items are associated with the entry -->
            <div
              v-if="entry.payload && entry.payload.items"
              class="d-inline-flex align-items-center"
            >
              <RewardItem
                v-for="reward in entry.payload.items"
                :key="reward.item.id"
                :item="reward.item"
                class="me-3"
              ></RewardItem>
            </div>
            <!-- social media icons and links -->
            <template v-if="socialMediaLinks.length > 0">
              <div class="d-inline-flex align-items-center mt-2">
                <span class="uppercase text-white me-3"
                  >{{ $t("events.button_more_info") }}:</span
                >
                <a
                  v-for="(link, index) in socialMediaLinks"
                  :key="'social-link-' + index"
                  :href="link.url"
                  target="_blank"
                  class="social-icon me-2 clickable"
                  :class="link.title.toLowerCase()"
                ></a></div
            ></template>
          </div>
        </div>
      </b-col>
    </b-row>
  </div>
</template>
<script>
import { marked } from "marked";
export default {
  name: "AnnouncementEntry",

  props: {
    entry: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      maxCharacters: 120,
      expanded: false,
      showLargeImage: false,
      largeImgUrl: "",
      socialMedia: ["Facebook", "Instagram", "WhatsApp"],
    };
  },

  computed: {
    customLink() {
      return (
        Object.values(this.entry.links || {}).find(
          (link) => !this.socialMedia.includes(link.title)
        )?.url || null
      );
    },
    socialMediaLinks() {
      return Object.entries(this.entry.links || {})
        .filter(([_, title]) => this.socialMedia.includes(title))
        .map(([url, title]) => ({ title, url }));
    },
    hasGallery() {
      return (
        this.entry.gallery &&
        Array.isArray(this.entry.gallery) &&
        this.entry.gallery.length > 0
      );
    },
    newsEntryText() {
      let text = this.entry.markdown_content || "";

      // Always convert full markdown → HTML first to avoid broken syntax
      const html = marked(text, { breaks: true });

      // Truncate the rendered HTML by visible text length for collapsed view
      if (text.length > this.maxCharacters && !this.expanded) {
        const div = document.createElement("div");
        div.innerHTML = html;
        const fullText = div.textContent || "";
        if (fullText.length > this.maxCharacters) {
          const truncated = fullText.slice(0, this.maxCharacters).trim() + "…";
          return "<p>" + truncated + "</p>";
        }
      }

      return html;
    },
    newsEntryImg() {
      if (this.entry.banner && this.entry.banner.length > 0) {
        return this.entry.banner[0].url;
      } else return require("@/assets/img/notification/icn-nc-lobby.png");
    },
    entryTimestamp() {
      return this.entry.published_at
        ? new Date(this.entry.published_at).toLocaleDateString(
            this.$store.state.locale
          )
        : "";
    },
    expandButtonText() {
      return this.expanded
        ? this.$t("news.show_less")
        : this.$t("news.show_more");
    },
    hasPrio() {
      return this.entry.priority > 0;
    },
    isUnread() {
      return this.$store.getters["news/unreadMessages"].some((msg) => {
        return msg.id === this.entry.id;
      });
    },
  },
  mounted() {
    if (this.hasPrio) {
      this.expanded = true;
      this.$store.commit("news/markAsRead", this.entry.id);
    }
  },
  methods: {
    openCustomLink() {
      if (!this.customLink) return;

      const normalizedUrl = /^https?:\/\//i.test(this.customLink)
        ? this.customLink
        : `https://${this.customLink}`;

      window.open(normalizedUrl, "_blank");
    },
    handleExpand() {
      this.expanded = !this.expanded;
      if (this.expanded) {
        this.$store.commit("news/markAsRead", this.entry.id);
        this.firebaseClickItemAnnouncement(this.entry.id);
      }
    },
    setLargeImage(url) {
      this.largeImgUrl = url;
      this.showLargeImage = true;
    },
    openLink(url) {
      const normalizedUrl = /^https?:\/\//i.test(url) ? url : `https://${url}`;
      window.open(normalizedUrl, "_blank");
    },
    getFavicon(url) {
      try {
        const { hostname } = new URL(url);
        return require("@/assets/img/common/default-fav.png");
      } catch (e) {
        return require("@/assets/img/common/default-fav.png");
      }
    },
    onFaviconError(event) {
      event.target.src = require("@/assets/img/common/default-fav.png");
    },
    getHostname(url) {
      try {
        return new window.URL(url).hostname;
      } catch (e) {
        return url;
      }
    },
  },
};
</script>
<style lang="scss">
.menu-list-entry {
  position: relative;
  &.gold-border-box {
    // Global gold-border-box style from Backgrounds.scss handles the visual styling
  }

  .announcement-entry.expanded {
    background: rgba(0, 0, 0, 0.15);
    border-radius: 4px;
  }

  .news-icon {
    max-width: 200px;
    max-height: 200px;
    border-radius: 5px;
  }

  .new-message-icon {
    width: 10px;
    height: 10px;
    min-width: 10px;
    background: $primary-accent;
    background-image: none;
    border-radius: 50%;
    margin-right: 8px;
    @keyframes unread-pulse {
      0%,
      100% {
        box-shadow: 0 0 4px rgba($primary-accent, 0.4);
      }
      50% {
        box-shadow: 0 0 12px rgba($primary-accent, 0.8),
          0 0 20px rgba($primary-accent, 0.3);
      }
    }
    animation: unread-pulse 2s ease-in-out infinite;
  }

  .news-text {
    text-align: left;

    .headline {
      font-size: 1.8rem;
      font-family: Ubuntu-Bold;
      line-height: 1;
      margin-bottom: 10px;
      .highlight {
        color: $text-highlight-yellow;
        text-shadow: 0 0 10px rgba($text-highlight-yellow, 0.5);
      }
      .timestamp {
        font-size: 12px;
        color: $text-gray;
        font-family: Ubuntu;
        white-space: nowrap;
      }
    }

    .subtext {
      margin-top: 10px;
      font-size: 1.5rem;
      font-family: Ubuntu;
      line-height: 1;
      padding-right: 20px;
    }
  }
  .news-text {
    .subtext {
      margin-top: 10px;
      font-size: 1.7rem;
      font-family: Ubuntu;
      line-height: 1.6;
      padding-right: 20px;
      color: #fff;

      p {
        margin-bottom: 0.8em;
      }

      ul,
      ol {
        margin: 0.8em 0 0.8em 1.5em;
      }

      a {
        color: $primary-accent;
        text-decoration: underline;
      }

      strong {
        font-weight: bold;
      }
    }
  }

  .social-icon {
    display: inline-block;
    width: 32px;
    height: 32px;
    background-size: cover;
    margin-right: 8px;
    transition: filter 0.2s ease;

    &:hover {
      filter: drop-shadow(0 0 6px rgba($primary-accent, 0.6));
    }

    &.facebook {
      background-image: url(~@/assets/img/common/facebook.png);
    }
    &.instagram {
      background-image: url(~@/assets/img/common/instagram.png);
    }
    &.whatsapp {
      background-image: url(~@/assets/img/common/whatsapp.png);
    }
  }
  .news-gallery {
    overflow-x: auto;
    overflow-y: hidden;
    white-space: nowrap;
    padding: 10px 0;

    .news-gallery-image {
      max-height: 100px;
      max-width: 100px;
      margin-right: 10px;
      border-radius: 5px;
      object-fit: cover;
      border: 1px solid transparent;
      transition: all 0.2s ease;

      &:hover {
        border-color: rgba($primary-accent, 0.5);
        transform: scale(1.05);
      }
    }
    .large-image {
      max-height: 80vh;
      max-width: 80vw;
      object-fit: contain;
    }
  }
  .related-links {
    background: rgba($primary-accent, 0.04);
    border-radius: 10px;
    padding: 15px 20px;
    border: 1px solid rgba($primary-accent, 0.12);

    h4 {
      font-family: Ubuntu-Bold;
      color: $text-highlight-yellow;
      font-size: 1.6rem;
    }
    .related-title {
      display: flex;
      align-items: center;
      gap: 8px;

      .related-icon {
        width: 22px;
        height: 22px;
        object-fit: contain;
      }
    }

    .link-card-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }

    .link-card {
      flex: 1 1 45%;
      min-width: 220px;
      display: flex;
      align-items: center;
      background: rgba(255, 255, 255, 0.08);
      border: 1px solid rgba(255, 255, 255, 0.1);
      border-radius: 8px;
      padding: 10px 15px;
      cursor: pointer;
      transition: all 0.2s ease-in-out;

      &:hover {
        transform: translateY(-2px);
        background: rgba(255, 255, 255, 0.12);
      }

      .icon-circle {
        background: rgba($primary-accent, 0.2);
        border-radius: 50%;
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;

        .favicon {
          width: 24px;
          height: 24px;
        }
      }

      .text {
        .title {
          color: #fff;
          font-weight: bold;
          font-size: 1rem;
        }
        .url {
          color: #aaa;
          font-size: 0.8rem;
        }
      }
    }
  }
}
.image-modal-dialog {
  margin: 0;
  max-width: none;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-modal-content {
  background: transparent !important;
  border: 0 !important;
  box-shadow: none !important;
  width: auto;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.image-modal-body {
  padding: 0 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  pointer-events: none;
}

.image-modal-img {
  width: auto;
  height: auto;
  max-width: 80vw;
  max-height: 80vh;
  object-fit: contain;
  display: block;
  pointer-events: auto;
  border-radius: 8px;
  transition: transform 0.2s ease-in-out;
}
</style>
